import React, { useMemo } from 'react'
import { classNameBuilder } from '@/helpers/classNameBuilder'
import translations from '@/translations'
import { TextSize as BaseTextSize } from '@/types/themeType'

type FontWeightType =
  | 'thin'
  | 'light'
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'extrabold'

type TransformType = 'uppercase' | 'capitalize' | 'lowercase'
type AlignType = 'left' | 'center' | 'justify' | 'right'
type ColorType =
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'
  | 'none'
type TextSize = BaseTextSize | '8' | '10' | '12' | '28' | 'none'

interface TextProps {
  size?: TextSize
  fontWeight?: FontWeightType
  transform?: TransformType
  align?: AlignType
  color?: ColorType
  className?: string
  children: React.ReactNode
}

interface TranslatedTextProps {
  translationKey: string
  size?: TextSize
  fontWeight?: FontWeightType
  transform?: TransformType
  align?: AlignType
  color?: ColorType
  className?: string
  values?: (string | number)[]
}

const TextComponent: React.FC<TextProps> = ({
  size = 'md',
  fontWeight = 'regular',
  transform,
  align = 'left',
  color = '500',
  className,
  children
}) => {
  return (
    <span
      className={classNameBuilder(
        !className?.includes('inline') && 'inline-block',
        textSizeMap[size],
        textColorMap[color],
        fontWeightMap[fontWeight],
        transformMap[transform || 'none'],
        alignMap[align],
        className
      )}
    >
      {children}
    </span>
  )
}

const Translated: React.FC<TranslatedTextProps> = ({
  translationKey,
  values,
  ...props
}) => {
  const label = useMemo(() => {
    // @ts-expect-error no types available for translations
    const translatedLabel = translations[translationKey]

    if (translatedLabel) {
      return values?.length
        ? translations.formatString(translatedLabel, ...values)
        : translatedLabel
    }

    return translationKey
  }, [translationKey, values])

  return <TextComponent {...props}>{label}</TextComponent>
}

interface TextWithTranslated extends React.FC<TextProps> {
  Translated: React.FC<TranslatedTextProps>
}

export const Text = TextComponent as TextWithTranslated
Text.Translated = Translated

export default Text

const fontWeightMap: Record<FontWeightType, string> = {
  thin: 'font-thin',
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold'
}

const textColorMap: Record<ColorType, string> = {
  200: 'text-gray-200',
  300: 'text-gray-300',
  400: 'text-gray-400',
  500: 'text-gray-500',
  600: 'text-gray-600',
  700: 'text-gray-700',
  800: 'text-gray-800',
  900: 'text-gray-900',
  none: ''
}

const transformMap: Record<TransformType | 'none', string> = {
  none: '',
  uppercase: 'uppercase',
  capitalize: 'capitalize',
  lowercase: 'lowercase'
}

const alignMap: Record<AlignType, string> = {
  left: 'text-left',
  center: 'text-center',
  justify: 'text-justify',
  right: 'text-right'
}

const textSizeMap: Record<TextSize, string> = {
  '8': 'text-[8px] leading-3',
  '10': 'text-[10px] leading-4',
  '12': 'text-xs leading-[18px]',
  '28': 'text-[28px] leading-[42px]',
  xs: 'text-[0.875rem] leading-[1.25rem]',
  sm: 'text-[1rem] leading-[1.5rem]',
  md: 'text-[1.125rem] leading-[1.75rem]',
  lg: 'text-[1.25rem] leading-[1.75rem]',
  xl: 'text-[1.5rem] leading-[2rem]',
  '2xl': 'text-[1.625rem] leading-[2rem]',
  '3xl': 'text-[2rem] leading-[2.25rem]',
  '4xl': 'text-[2.375rem] leading-[2.5rem]',
  '5xl': 'text-[3.125rem] leading-none',
  '6xl': 'text-[3.875rem] leading-none',
  '7xl': 'text-[4.5rem] leading-none',
  '8xl': 'text-[6.125rem] leading-none',
  '9xl': 'text-[8.125rem] leading-none',
  none: ''
}
